import { startTransition, useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Modal, useModalContext } from "../../common/Modal";
import { extractAdFormConfig, getAdFormConfig } from "../../../lib/leboncoin/LeboncoinExtApi";
import { gql, useMutation } from '@apollo/client';
import { checkLeboncoinListingVariationsConfigurationCompleteness, ExternalListingConfiguration } from '../../../../common/listingVariationsConfiguration';
import { useRequestManager } from '../../common/RequestManager';
import { EditListingConfigurationModal_ImportAliexpressExternalListingIdsMutation, EditListingConfigurationModal_ImportAliexpressExternalListingIdsMutationVariables, EditListingConfigurationModal_ImportTemuExternalListingIdsMutation, EditListingConfigurationModal_ImportTemuExternalListingIdsMutationVariables, EditListingConfigurationModal_UpdateExternalListingMutation, EditListingConfigurationModal_UpdateExternalListingMutationVariables, ExternalListingRow_ExternalListingFragment, ListingsSection_AdFragment, UpdateAdExternalListingInput } from '../../../../codegen/graphql';
import { TextInput } from '../../common/TextInput';
import { ButtonGroup } from '../../common/ButtonGroup';
import { SupplierName } from '../../../../common/suppliersConfig';
import AdVariation_SearchSelect from '../../common/SearchSelect/AdVariation_SearchSelect';
import EditListingConfigurationModal_Variations from './EditListingConfigurationModal_Variations';
import { EbayAttributesSection, EBAY_ATTRIBUTES_SECTION_FRAGMENT } from '../EbayAttributesSection';
import { LeboncoinAttributesSection, LEBONCOIN_ATTRIBUTES_SECTION_FRAGMENT } from '../LeboncoinAttributesSection';

type EditListingConfigurationModalProps = {
  adData: ListingsSection_AdFragment;
  listing: ExternalListingRow_ExternalListingFragment;
  listingConfigurationWithDefaults: ExternalListingConfiguration;
  configurationCheck: ReturnType<typeof checkLeboncoinListingVariationsConfigurationCompleteness>;
  onClose: () => void;
}

export const IMPORT_TEMU_EXTERNAL_LISTING_IDS_MUTATION = gql`
  mutation EditListingConfigurationModal_ImportTemuExternalListingIds($externalListingId: ID!, $goodsId: String!) {
    importTemuExternalListingIds(externalListingId: $externalListingId, goodsId: $goodsId) {
      id
      externalId
      configuration {
        variationsConfig {
          variations
        }
      }
    }
  }
`;


export const IMPORT_ALIEXPRESS_EXTERNAL_LISTING_IDS_MUTATION = gql`
  mutation EditListingConfigurationModal_ImportAliexpressExternalListingIds($externalListingId: ID!, $productId: String!) {
    importAliexpressExternalListingIds(externalListingId: $externalListingId, productId: $productId) {
      id
      externalId
      configuration {
        variationsConfig {
          variations
        }
      }
    }
  }
`;


const EDIT_LISTING_CONFIGURATION_MODAL_UPDATE_EXTERNAL_LISTING_MUTATION = gql`
  mutation EditListingConfigurationModal_UpdateExternalListing($id: ID!, $input: UpdateAdExternalListingInput!) {
    updateAdExternalListing(id: $id, input: $input) {
      id
      adId
      lang
      externalId
      externalData
      configuration {
        isMultiVariation
        useSingleVariationId
        supplierKey
        variationsConfig {
          types
          values
          variations
        }
      }
      lastSyncAt
      ...EbayAttributesSection_listing
      ...LeboncoinAttributesSection_listing
    }
  }
  ${EBAY_ATTRIBUTES_SECTION_FRAGMENT}
  ${LEBONCOIN_ATTRIBUTES_SECTION_FRAGMENT}
`;

export default function EditListingConfigurationModal({
  adData,
  listing,
  listingConfigurationWithDefaults,
  configurationCheck,
  onClose,
}: EditListingConfigurationModalProps) {
  return (
    <Modal
      onClose={onClose}
      title={`Edit Listing Configuration : ${listing.lang} - ${listing.externalId}`}
      width="fullscreen"
    >
      <EditListingConfigurationModalContent
        adData={adData}
        listing={listing}
        listingConfigurationWithDefaults={listingConfigurationWithDefaults}
        configurationCheck={configurationCheck}
        onClose={onClose}
      />
    </Modal>
  );
}

function EditListingConfigurationModalContent({
  adData,
  listing,
  listingConfigurationWithDefaults,
  configurationCheck,
  onClose,
}: {
  adData: ListingsSection_AdFragment;
  listing: ExternalListingRow_ExternalListingFragment;
  listingConfigurationWithDefaults: ExternalListingConfiguration;
  configurationCheck: ReturnType<typeof checkLeboncoinListingVariationsConfigurationCompleteness>;
  onClose: () => void;
}) {
  const { trackRequest } = useRequestManager();
  const modalContext = useModalContext();
  const [updateExternalListingMutation] = useMutation<
    EditListingConfigurationModal_UpdateExternalListingMutation,
    EditListingConfigurationModal_UpdateExternalListingMutationVariables
  >(
    EDIT_LISTING_CONFIGURATION_MODAL_UPDATE_EXTERNAL_LISTING_MUTATION
  );
  const [importTemuExternalListingIdsMutation] = useMutation<
    EditListingConfigurationModal_ImportTemuExternalListingIdsMutation,
    EditListingConfigurationModal_ImportTemuExternalListingIdsMutationVariables
  >(IMPORT_TEMU_EXTERNAL_LISTING_IDS_MUTATION);

  const [importAliexpressExternalListingIdsMutation] = useMutation<
    EditListingConfigurationModal_ImportAliexpressExternalListingIdsMutation,
    EditListingConfigurationModal_ImportAliexpressExternalListingIdsMutationVariables
  >(IMPORT_ALIEXPRESS_EXTERNAL_LISTING_IDS_MUTATION);

  const importTemuExternalListingIds = useCallback(() => {
    trackRequest(
      importTemuExternalListingIdsMutation({
        variables: {
          externalListingId: listing.id,
          goodsId: listing.externalId!,
        },
      }),
      {
        title: 'Importing Temu external listing IDs',
        isBlocking: false,
      }
    );
  }, [listing.id, listing.externalId, importTemuExternalListingIdsMutation, trackRequest]);


  const importAliexpressExternalListingIds = useCallback(async () => {
    trackRequest(
      importAliexpressExternalListingIdsMutation({
        variables: {
          externalListingId: listing.id,
          productId: listing.externalId!,
        },
      }),
      {
        title: 'Importing Aliexpress external listing IDs',
        isBlocking: false,
      }
    );
  }, [listing.id, listing.externalId, importAliexpressExternalListingIdsMutation, trackRequest]);
  // Handle configuration update
  const updateListing = useCallback(async (
    field: string,
    value: any
  ) => {
    await trackRequest(
      updateExternalListingMutation({ variables: { id: listing.id, input: { [field]: value } } }),
      {
        title: `Updating listing ${field}`,
        isBlocking: false,
      }
    );
  }, [updateExternalListingMutation, listing.id]);

  const updateListingConfiguration = useCallback(async (input: Partial<ExternalListingConfiguration>) => {
    const newConfiguration: UpdateAdExternalListingInput = {
      configuration: {
        ...listing.configuration,
        ...input,
      },
    };
    
    trackRequest(
      updateExternalListingMutation({ variables: { id: listing.id, input: newConfiguration } }),
      {
        title: 'Updating listing configuration',
        isBlocking: false,
      }
    );
  }, [updateExternalListingMutation, listing.id, listing.configuration]);

  const updateExternalData = useCallback(async (externalData: Record<string, any>) => {
    trackRequest(
      updateExternalListingMutation({
        variables: {
          id: listing.id,
          input: { externalData },
        },
      }),
      {
        title: 'Updating listing attributes',
        isBlocking: false,
      }
    );
  }, [updateExternalListingMutation, listing.id, trackRequest]);

  const [leboncoinFormConfig, setLeboncoinFormConfig] = useState<ReturnType<typeof extractAdFormConfig> | null>(null);
  useEffect(() => {
    if (listing.externalData?.category?.id) {
      getAdFormConfig(listing.externalData.category.id)
        .then(extractAdFormConfig)
        .then(setLeboncoinFormConfig);
    } else {
      setLeboncoinFormConfig(null);
    }
  }, [listing.externalData?.category?.id]);

  useLayoutEffect(() => {
    modalContext.setActions([
      {
        children: 'Save',
        preset: 'primary',
        onClick: () => {
          onClose();
        },
      },
    ]);
  }, []);

  const singleAdVariation = useMemo(() => {
    if (
      listing.configuration?.isMultiVariation === false
      && listing.configuration?.useSingleVariationId
    ) {
      return adData.variations.find(v => v.id === listing.configuration?.useSingleVariationId);
    }
    return null;
  }, [listing.configuration?.isMultiVariation, listing.configuration?.useSingleVariationId, adData.variations]);


  const supplierKey = listing.configuration?.supplierKey;


  return (
    <div className="flex flex-col">
      <div className="flex flex-row gap-2">
        <TextInput
          label="External ID"
          defaultValue={listing.externalId || ''}
          onChange={(e) => updateListing('externalId', e.target.value)}
          fullWidth={false}
        />
    
        <ButtonGroup<SupplierName>
          label="Supplier Type"
          options={[
            { value: SupplierName.FR_ilyes, label: 'FR_ilyes' },
            { value: SupplierName.aliexpress, label: 'aliexpress' },
          ]}
          value={supplierKey as SupplierName}
          onChange={(supplierKey) => updateListingConfiguration({ supplierKey })}
        />
        <br/>
        <ButtonGroup
          label="Listing Variation Type"
          options={[
            { value: false, label: 'Single Variation' },
            { value: true, label: 'Multi Variations' },
          ]}
          value={listing.configuration?.isMultiVariation}
          onChange={(isMultiVariation) => updateListingConfiguration({ isMultiVariation })}
        />

        {listing.configuration?.isMultiVariation === false && (
          <AdVariation_SearchSelect
            selectedAdVariation={singleAdVariation || null}
            onAdVariationChange={(adVariation) => updateListingConfiguration({ useSingleVariationId: adVariation?.id })}
            adId={adData.id}
          />
        )}
      </div>
      
      {configurationCheck.isValid === false ? (
        <div className="text-lg text-orange-700 mb-4">
          Configuration is invalid. {
            Object.keys(configurationCheck.typesErrors).length
            + Object.keys(configurationCheck.valuesErrors).length
            + Object.keys(configurationCheck.variationsErrors).length
          } errors found.
        </div>
      ) : (
        <div className="text-lg text-green-700 mb-4">
          Configuration is valid.
        </div>
      )}

      {listingConfigurationWithDefaults.supplierKey !== null
      && (
        listingConfigurationWithDefaults.isMultiVariation === true
        || (
          listingConfigurationWithDefaults.isMultiVariation === false
          && listingConfigurationWithDefaults.useSingleVariationId !== null
        )
      ) && (
        <EditListingConfigurationModal_Variations
          updateListingConfiguration={updateListingConfiguration}
          listingConfigurationWithDefaults={listingConfigurationWithDefaults}
          adData={adData}
          leboncoinAdFormConfig={leboncoinFormConfig}
          listing={listing}
          importTemuExternalListingIds={importTemuExternalListingIds}
          importAliexpressExternalListingIds={importAliexpressExternalListingIds}
          configurationCheck={configurationCheck}
        />
      )}

      {listing.shop.shopType === 'ebay' && (
        <EbayAttributesSection
          listing={listing}
          onExternalDataChange={updateExternalData}
        />
      )}

      {listing.shop.shopType === 'leboncoin' && (
        <LeboncoinAttributesSection
          listing={listing}
          onExternalDataChange={updateExternalData}
          fieldsKeys={leboncoinFormConfig?.formItemsKeys}
          itemsDefinitions={leboncoinFormConfig?.itemsDefinition}
        />
      )}
    </div>
  );
}